import { toggleClass } from 'utils/toggle-classlist'
import currencyHelper from 'services/currency-helper'
import { t9n } from 't9n/t9n'

interface CustomSelectEvent extends Event {
  value: string
}

// Implement these selectors in HTML
// data-js-selector="dropdown-wrapper"
// data-js-selector="dropdown-list"
// data-js-selector="dropdown-native-select"
// data-js-selector="selected-option"
// data-dropdown-item-value
// data-select-template

// Optional
// data-fancy-wrapper-class - Here goes extra classes for fancy wrapper

const $dropdownWrappers = document.querySelectorAll<HTMLElement>('[data-js-selector="dropdown-wrapper"]')

export default function initVanillaSelect(): void {
  for (const $dropdown of $dropdownWrappers) {
    renderSelect($dropdown)
    handleToggling($dropdown)
    window.addEventListener('click', (event) => closeSelectOnOutsideClick($dropdown, event))
    selectItemsHandling($dropdown)
    handleSearching($dropdown)
    handleChangeByEvent($dropdown)
  }
}

function renderSelect(dropdown: HTMLElement): void {
  renderSelectWrapper(dropdown)
  renderSearch(dropdown)
  renderSelectOptions(dropdown)
}

function renderSelectWrapper(dropdown: HTMLElement): void {
  const $nativeSelect = dropdown.querySelector('[data-js-selector="dropdown-native-select"]') as HTMLSelectElement
  const selectWrapper = `
    <nav
      class="bem-select-dropdown__list ${$nativeSelect.dataset.fancyWrapperClass}"
      aria-labelledby="dropdown"
      data-js-selector="dropdown-list"
    ></nav>
  `
  dropdown.insertAdjacentHTML('beforeend', selectWrapper)
}

function renderSelectOptions(dropdown: HTMLElement): void {
  const $selectWrapper = dropdown.querySelector('[data-js-selector="dropdown-list"]') as HTMLElement
  const options = dropdown.querySelectorAll<HTMLElement>('[data-js-selector="dropdown-native-select"] option')
  const selectOptions = Array.from(options).map((option) => option.dataset.selectTemplate)
  for (const selectOption of selectOptions) {
    $selectWrapper.insertAdjacentHTML('beforeend', selectOption)
  }
}

function renderSearch(dropdown: HTMLElement): void {
  const $selectWrapper = dropdown.querySelector<HTMLElement>('[data-js-selector="dropdown-list"]')
  const searchLabel = `
    <div class="bem-select-dropdown__search">
      <input placeholder=${t9n(
        'preactSelect.search',
      )} type="text" class="bem-select-dropdown__search-input" data-js-selector="dropdown-search">
    </div>
  `
  $selectWrapper.insertAdjacentHTML('beforeend', searchLabel)
}

function toggleSelect(dropdown: HTMLElement): void {
  const $list = dropdown.querySelector<HTMLElement>('[data-js-selector="dropdown-list"]')
  const $button = dropdown.querySelector<HTMLElement>('[data-js-selector="dropdown-select-button"]')
  const $search = dropdown.querySelector<HTMLElement>('[data-js-selector="dropdown-search"]')
  toggleClass($list, 'is-open')
  toggleClass($button, 'is-open')

  if ($list.classList.contains('is-open')) {
    clearFilter(dropdown)
    $search.focus()
  }
}

function closeSelectOnOutsideClick(dropdown: HTMLElement, event: MouseEvent): void {
  const $list = dropdown.querySelector<HTMLElement>('[data-js-selector="dropdown-list"]')
  const $button = dropdown.querySelector<HTMLElement>('[data-js-selector="dropdown-select-button"]')
  if (!dropdown.contains(event.target as Node) && $list.classList.contains('is-open')) {
    $list.classList.remove('is-open')
    $button.classList.remove('is-open')
  }
}

function selectItemsHandling(dropdown: HTMLElement): void {
  const $items = dropdown.querySelectorAll<HTMLElement>('[data-dropdown-item-value]')
  for (const $item of $items) {
    $item.addEventListener('click', () => selectItem($item))
  }
}

function handleSearching(dropdown: HTMLElement): void {
  const $search = dropdown.querySelector<HTMLInputElement>('[data-js-selector="dropdown-search"]')
  $search.addEventListener('input', (event: KeyboardEvent) => {
    const target = event.target as HTMLInputElement
    filterOptions(target.value, dropdown)
  })
}

function clearFilter(dropdown: HTMLElement): void {
  const $search = dropdown.querySelector<HTMLInputElement>('[data-js-selector="dropdown-search"]')
  $search.value = ''
  filterOptions('', dropdown)
}

function filterOptions(value: string, dropdown: HTMLElement): void {
  const $items = dropdown.querySelectorAll<HTMLElement>('[data-dropdown-item-value]')
  for (const $item of $items) {
    const itemParent = $item.parentNode as HTMLElement
    const currency = $item.dataset.dropdownItemValue
    const val = value.toLowerCase().trim()
    $item.hidden =
      !isElementFiltered(val, currency) && itemParent.getAttribute('data-js-selector') !== 'selected-option'
  }
}

function isElementFiltered(value: string, currency: string): boolean {
  const curr = currency.toLowerCase().trim()
  const currencyName = currencyHelper.getCurrencyName(currency).toLowerCase().trim()
  const currencyCountry = currencyHelper.getCurrencyCountry(currency).toLowerCase().trim()
  return !value || curr.includes(value) || currencyName.includes(value) || currencyCountry.includes(value)
}

function handleToggling(dropdown: HTMLElement): void {
  const $dropdownButton = dropdown.querySelector<HTMLElement>('[data-js-selector="dropdown-select-button"]')
  $dropdownButton.addEventListener('click', () => toggleSelect(dropdown))
}

function handleChangeByEvent(dropdown: HTMLElement): void {
  const selectName = dropdown.querySelector('[data-js-selector="dropdown-native-select"]').getAttribute('id')
  window.addEventListener(selectName, (event: CustomEvent<CustomSelectEvent>) => {
    changeNativeSelectValue(dropdown, event.detail.value)
    replaceSelectedValue(dropdown)
    propagateSelectChange(dropdown)
  })
}

function selectItem(item: HTMLElement): void {
  const itemValue = item.dataset.dropdownItemValue
  const $dropdown = item.closest('[data-js-selector="dropdown-wrapper"]') as HTMLElement

  changeNativeSelectValue($dropdown, itemValue)
  replaceSelectedValue($dropdown)
  propagateSelectChange($dropdown)
}

function replaceSelectedValue(dropdown: HTMLElement): void {
  const $nativeSelect = dropdown.querySelector('[data-js-selector="dropdown-native-select"]') as HTMLSelectElement
  const chosenOption = Array.from($nativeSelect.children).find(
    (option: HTMLOptionElement) => option.selected,
  ) as HTMLOptionElement
  dropdown.querySelector('[data-js-selector="selected-option"]').innerHTML = chosenOption.dataset.selectTemplate
}

function changeNativeSelectValue(dropdown: HTMLElement, itemValue: string): void {
  const $nativeSelect = dropdown.querySelector('[data-js-selector="dropdown-native-select"]') as HTMLSelectElement
  $nativeSelect.value = itemValue
  onNativeSelectValueChange(dropdown)
}

function onNativeSelectValueChange(dropdown: HTMLElement): void {
  const $list = dropdown.querySelector<HTMLElement>('[data-js-selector="dropdown-list"]')
  const $button = dropdown.querySelector<HTMLElement>('[data-js-selector="dropdown-select-button"]')
  $list.classList.remove('is-open')
  $button.classList.remove('is-open')
  clearFilter(dropdown)
}

function propagateSelectChange(dropdown: HTMLElement): void {
  const selectName = dropdown.querySelector('[data-js-selector="dropdown-native-select"]').getAttribute('id')
  window.dispatchEvent(new Event(`${selectName} change`))
}
